import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector, ReversibleEffector } from "./EffectorBase";
import { CustomEventTypes, EasingFunctionTypes, PropertyTypes, SaveModificationTypes } from "../../../globals";
import Vector3Property from "../../../components/properties/Vector3Property";
import NumberProperty from "../../../components/properties/NumberProperty";
import Engine from '../../Engine';
import ProjectSaver from '../../ProjectSaver';
import { TimelineFragment } from '../TimelineFragment';
import { areArraysEqual } from '../../utilities/Misc';

const normalizedAxis = new THREE.Vector3();

export interface EffectorTransformRotateSelfSaveData
{
	axis: { x: number, y: number, z: number };
	angle: number;
	easing: EasingFunctionTypes;
	reversed: boolean;
	loopCount: number;
}

export default class EffectorTransformRotateSelf extends EffectorBase<EffectorTransformRotateSelf> implements LoopableEffector, ReversibleEffector
{
	axis: THREE.Vector3;
	angle: number;
	easing: EasingFunctionTypes;
	deltaRotation: THREE.Quaternion;
	reversed: boolean;
	loopCount: number;
	defaultValues: { axis: { x: number, y: number, z: number }, angle: number };

	constructor(fragment : TimelineFragment)
	{
		super(fragment);

		this.axis = new THREE.Vector3(0, 0, 0);
		this.angle = 0.0;
		this.easing = EasingFunctionTypes.Linear;
		this.deltaRotation = new THREE.Quaternion();
		this.reversed = false;
		this.loopCount = 1;
		this.defaultValues = {
			axis: { x: 0, y: 0, z: 0 },
			angle: 0.0,
		};
	}

	save = () : EffectorTransformRotateSelfSaveData =>
	{
		const output = {
			axis: { x: this.axis.x, y: this.axis.y, z: this.axis.z },
			angle: this.angle,
			easing: this.easing,
			reversed: this.reversed,
			loopCount: this.loopCount,
		};

		return output;
	};

	load = (savedData: EffectorTransformRotateSelfSaveData) =>
	{
		this.axis.set(savedData.axis.x, savedData.axis.y, savedData.axis.z);
		this.angle = savedData.angle;
		this.easing = savedData.easing;
		if (savedData.reversed !== undefined) this.reversed = savedData.reversed;
		if (savedData.loopCount !== undefined) this.loopCount = savedData.loopCount;
	};

	copyValues = (sourceEffector: EffectorTransformRotateSelf) =>
	{
		this.angle = sourceEffector.angle;
		this.axis.copy(sourceEffector.axis);
		this.easing = sourceEffector.easing;
		this.reversed = sourceEffector.reversed;
		this.loopCount = sourceEffector.loopCount;
	}

	setAngle = (value: number) =>
	{
		this.angle = value;
	}

	setAxis = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.axis.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.axis.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.axis.z = z;
	}

	resetState = ()	=>
	{		
		if (!this.fragment.previousTime || !this.angle) return;

		this.deltaRotation.invert();
		this.forEachTargetObject((targetObject) => 
		{
			targetObject.quaternion.multiply(this.deltaRotation);
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	update = (fragmentTime: number) =>
	{
		if (!fragmentTime || !this.angle || !this.axis.lengthSq()) return;

		normalizedAxis.copy(this.axis).normalize();
		this.deltaRotation.setFromAxisAngle(normalizedAxis, (this.angle * fragmentTime) / 180 * Math.PI);

		this.forEachTargetObject((targetObject) =>
		{

			targetObject.quaternion.multiply(this.deltaRotation);
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectRotationChanged);
		}
	}

	onAdded = () =>
	{
		for (const object of this.targetObjects())
		{
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Rotation);
		}
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Angle",
				PropertyTypes.Number,
				<NumberProperty
					value={this.angle}
					step={45}
					defaultValue={this.defaultValues.angle}
					decimals={1}
				/>,
				() => {return this.angle;},
				(v) => {this.setAngle(Number(v));},
			),
			new EffectorPropertyDrawer(
				"Axis (Local)",
				PropertyTypes.Vector3,
				<Vector3Property
					value={[this.axis.x, this.axis.y, this.axis.z]}
					renderChildrenInSeparateRows={true}
					defaultValue={this.defaultValues.axis}
					decimals={1}
				/>,
				() => {return [this.axis.x, this.axis.y, this.axis.z];},
				(v) => {this.setAxis(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
		];
	}
}
